import { Column } from './BillItems';
import { BillItem } from './types';

/**
 * Shared Column Configuration for Insurance Bills
 * 
 * This file defines the columns used in both:
 * - InsuranceBillView (UI table)
 * - InsuranceBillPDFDocument (PDF table)
 * 
 * When you modify these columns, both the view and PDF will automatically update.
 */

export const INSURANCE_BILL_COLUMNS: Column[] = [
    { name: "patientBillCreatedAt" as keyof BillItem, type: "date", validation: "" }, // Patient bill creation date
    { name: "patientName" as keyof BillItem, type: "text", validation: "" }, // Patient Name column
    { name: "patientBillId" as keyof BillItem, type: "text", validation: "" }, // Patient Bill ID column
    { name: "souscripteur" as keyof BillItem, type: "text", validation: "", translationKey: "hospital.billing.insurance_bills.souscripteur" }, // Souscripteur covered under the organisme (SHER-713)
    { name: "concept", type: "text", validation: "" },
    { name: "quantity", type: "number", validation: "" },
    { name: "total" as keyof BillItem, type: "amount", validation: "", translationKey: "hospital.billing.bill.total" }, // Total bill amount (patientTotalBill from insuranceClaim)
    { name: "patientPaymentAmount" as keyof BillItem, type: "amount", validation: "" }, // Patient payment (patientTotalBill - approvedAmount)
    { name: "amount", type: "amount", validation: "", translationKey: "hospital.billing.insurance_bills.insurance_amount" } // Approved amount (approvedAmount from insuranceClaim)
];

/**
 * Column metadata for PDF rendering
 * Defines how each column should be displayed in the PDF table
 */
export interface PDFColumnConfig {
    name: string;
    width: string;
    textAlign?: 'left' | 'right' | 'center';
    translationKey: string;
    isTotalColumn?: boolean; // Whether this column should show totals
}

/**
 * PDF column metadata configuration
 * Maps column names to their PDF rendering properties
 * The order is automatically derived from INSURANCE_BILL_COLUMNS
 */
const PDF_COLUMN_METADATA: Record<string, Omit<PDFColumnConfig, 'name'>> = {
    "patientBillCreatedAt": {
        width: "10%",
        textAlign: "center",
        translationKey: "hospital.billing.item.patientBillCreatedAt"
    },
    "patientName": {
        width: "15%",
        textAlign: "left",
        translationKey: "hospital.billing.item.patientName"
    },
    "patientBillId": {
        width: "8%",
        textAlign: "center",
        translationKey: "hospital.billing.insurance_bills.patient_bill_id"
    },
    "souscripteur": {
        width: "11%",
        textAlign: "left",
        translationKey: "hospital.billing.insurance_bills.souscripteur",
        isTotalColumn: false
    },
    "concept": {
        width: "14%",
        textAlign: "left",
        translationKey: "hospital.billing.item.concept"
    },
    "quantity": {
        width: "7%",
        textAlign: "center",
        translationKey: "hospital.billing.item.quantity"
    },
    "total": {
        width: "12%",
        textAlign: "center",
        translationKey: "hospital.billing.bill.total",
        isTotalColumn: false
    },
    "patientPaymentAmount": {
        width: "11%",
        textAlign: "center",
        translationKey: "hospital.billing.insurance_bills.patient_payment_amount",
        isTotalColumn: false
    },
    "amount": {
        width: "12%",
        textAlign: "right",
        translationKey: "hospital.billing.insurance_bills.insurance_amount",
        isTotalColumn: true
    }
};

/**
 * Dynamically generates PDF columns based on INSURANCE_BILL_COLUMNS order
 * This ensures the PDF table columns match the app table columns order automatically
 * When you change the order in INSURANCE_BILL_COLUMNS, the PDF will update automatically
 */
export const INSURANCE_BILL_PDF_COLUMNS: PDFColumnConfig[] = INSURANCE_BILL_COLUMNS.map((col) => {
    const columnName = col.name as string;
    const metadata = PDF_COLUMN_METADATA[columnName];
    
    if (!metadata) {
        console.warn(`[insuranceBillColumns] Missing PDF metadata for column: ${columnName}`);
        // Fallback configuration
        return {
            name: columnName,
            width: "10%",
            textAlign: "left" as const,
            translationKey: col.translationKey || `hospital.billing.item.${columnName}`,
            isTotalColumn: false
        };
    }
    
    return {
        name: columnName,
        ...metadata,
        // Use translationKey from column if provided, otherwise use metadata
        translationKey: col.translationKey || metadata.translationKey
    };
});
